import React from 'react';
import { useTranslate } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { CustomEmptyState } from './CustomEmptyState';

interface EmptyStateConfig {
  resource: string;
  title: string;
  subtitle: string;
  actionText: string;
  navigationPath: string;
}

const emptyStateConfigs: Record<string, EmptyStateConfig> = {
  drivers: {
    resource: 'drivers',
    title: 'No Drivers Yet',
    subtitle:
      'Add drivers to manage their profiles, licenses, certifications, and employment history. Build your driver database.',
    actionText: 'Add Driver',
    navigationPath: '/drivers/wizard',
  },
  users: {
    resource: 'users',
    title: 'No Users Yet',
    subtitle:
      'Add team members and manage user accounts. Users can access the system with different roles and permissions.',
    actionText: 'Add User',
    navigationPath: '/users/create',
  },
  forms: {
    resource: 'forms',
    title: 'No Forms Yet',
    subtitle:
      'Create custom forms for data collection, inspections, and documentation. Build your workflow with digital forms.',
    actionText: 'Create Form',
    navigationPath: '/configuration/forms/create',
  },
  'completed-forms': {
    resource: 'completed-forms',
    title: 'No Completed Forms',
    subtitle: 'Form submissions and responses will appear here once users start completing forms.',
    actionText: 'Manage form templates',
    navigationPath: '/configuration/forms',
  },
  equipment: {
    resource: 'equipment',
    title: 'No Equipment Yet',
    subtitle:
      'Track vehicles, trucks, trailers, and other equipment. Manage your fleet inventory and maintenance records.',
    actionText: 'Add Equipment',
    navigationPath: '/equipment/create',
  },
  'driver-employment': {
    resource: 'driver-employment',
    title: 'No Employment History',
    subtitle:
      'Employment records will appear here when added to driver profiles. Track work history and references.',
    actionText: 'Add Employment',
    navigationPath: '/driver-employment/create',
  },
  'driver-certifications': {
    resource: 'driver-certifications',
    title: 'No Certifications',
    subtitle: 'Driver certifications and training records will appear here when added to profiles.',
    actionText: 'Add Certification',
    navigationPath: '/driver-certifications/create',
  },
  'equipment-maintenance': {
    resource: 'equipment-maintenance',
    title: 'No Maintenance Records',
    subtitle:
      'Maintenance history and service records will appear here. Track repairs, inspections, and scheduled maintenance.',
    actionText: 'Add Maintenance Record',
    navigationPath: '/equipment-maintenance/create',
  },
  maintenance: {
    resource: 'equipment-maintenance',
    title: 'No Maintenance Records',
    subtitle:
      'Maintenance history and service records will appear here. Track repairs, inspections, and scheduled maintenance.',
    actionText: 'Add Maintenance Record',
    navigationPath: '/equipment-maintenance/create',
  },
  'equipment-fuel': {
    resource: 'equipment-fuel',
    title: 'No Fuel Records',
    subtitle:
      'Fuel purchases and consumption tracking will appear here. Monitor fuel efficiency and costs.',
    actionText: 'Add Fuel Record',
    navigationPath: '/equipment-fuel/create',
  },
  'fuel records': {
    resource: 'equipment-fuel',
    title: 'No Fuel Records',
    subtitle:
      'Fuel purchases and consumption tracking will appear here. Monitor fuel efficiency and operating costs.',
    actionText: 'Add Fuel Record',
    navigationPath: '/equipment-fuel/create',
  },
  'equipment-inspections': {
    resource: 'equipment-inspections',
    title: 'No Inspections',
    subtitle:
      'Safety inspections and compliance checks will appear here. Stay on top of regulatory requirements.',
    actionText: 'Add Inspection',
    navigationPath: '/equipment-inspections/create',
  },
  inspections: {
    resource: 'equipment-inspections',
    title: 'No Inspections',
    subtitle:
      'Safety inspections and compliance checks will appear here. Stay on top of regulatory requirements.',
    actionText: 'Add Inspection',
    navigationPath: '/equipment-inspections/create',
  },
  'equipment-assignments': {
    resource: 'equipment-assignments',
    title: 'No Assignments',
    subtitle:
      'Equipment assignment history will appear here. Track who used which equipment and when.',
    actionText: 'Add Assignment',
    navigationPath: '/equipment-assignments/create',
  },
  'equipment-incidents': {
    resource: 'equipment-incidents',
    title: 'No Incidents',
    subtitle:
      'Incident reports and damage records will appear here. Document accidents, breakdowns, and repairs.',
    actionText: 'Add Incident',
    navigationPath: '/equipment-incidents/create',
  },
  customers: {
    resource: 'customers',
    title: 'No Customers Yet',
    subtitle:
      'Build your customer database by adding contacts, tracking service history, and managing relationships.',
    actionText: 'Add Customer',
    navigationPath: '/customers/create',
  },
  leads: {
    resource: 'leads',
    title: 'No Leads Yet',
    subtitle:
      'Generate business by tracking potential customers. Monitor lead sources and convert them into paying customers.',
    actionText: 'Add Lead',
    navigationPath: '/leads/create',
  },
  opportunities: {
    resource: 'opportunities',
    title: 'No Opportunities Yet',
    subtitle:
      'Track your sales pipeline and potential deals. Monitor progress and convert leads into revenue.',
    actionText: 'Create Opportunity',
    navigationPath: '/opportunities/create',
  },
  estimates: {
    resource: 'estimates',
    title: 'No Estimates Yet',
    subtitle:
      'Create professional estimates for your services. Track approval status and convert estimates into invoices.',
    actionText: 'Create Estimate',
    navigationPath: '/estimates/create',
  },
  bills: {
    resource: 'bills',
    title: 'No Bills Yet',
    subtitle:
      'Track expenses and vendor bills. Manage your payables and keep organized records of business expenses.',
    actionText: 'Add Bill',
    navigationPath: '/bills/create',
  },
  invoices: {
    resource: 'invoices',
    title: 'No Invoices Yet',
    subtitle:
      'Generate and send professional invoices to customers. Track payment status and manage your billing process.',
    actionText: 'Create Invoice',
    navigationPath: '/invoices/create',
  },
  payments: {
    resource: 'payments',
    title: 'No Payments Yet',
    subtitle:
      'Payment records and transaction history will appear here. Track received payments and outstanding balances.',
    actionText: 'View Invoices',
    navigationPath: '/invoices',
  },
  'crm/payments': {
    resource: 'crm/payments',
    title: 'No Payments Yet',
    subtitle:
      'Payment records and transaction history will appear here. Track received payments and outstanding balances.',
    actionText: 'View Invoices',
    navigationPath: '/invoices',
  },
  routes: {
    resource: 'routes',
    title: 'No Routes Yet',
    subtitle:
      'Create and optimize delivery routes for your drivers. Track route performance and improve efficiency.',
    actionText: 'Create Route',
    navigationPath: '/routes/create',
  },
  boards: {
    resource: 'boards',
    title: 'No Boards Yet',
    subtitle:
      'Create kanban boards to organize your workflow. Track opportunities, projects, and tasks visually.',
    actionText: 'Create Board',
    navigationPath: '/boards/create',
  },
  'service-providers': {
    resource: 'service-providers',
    title: 'No Service Providers Yet',
    subtitle:
      'Manage your network of contractors and service providers. Track contact information and service capabilities.',
    actionText: 'Add Service Provider',
    navigationPath: '/service-providers/create',
  },
  'timetracking-activities': {
    resource: 'timetracking-activities',
    title: 'No Time Activities Yet',
    subtitle:
      'Define activity types for time tracking and timer segments. Crew can start timers and log time against these activities in the field.',
    actionText: 'Create Activity',
    navigationPath: '/timetracking/activities/create',
  },
  'timetracking/activities': {
    resource: 'timetracking-activities',
    title: 'No Time Activities Yet',
    subtitle:
      'Define activity types for crew timers, job costing, and time reports before field teams start logging time.',
    actionText: 'Create Activity',
    navigationPath: '/timetracking/activities/create',
  },
};

export const createEmptyStateComponent = (resourceKey: string) => {
  return () => {
    const navigate = useNavigate();
    const translate = useTranslate();

    const config = emptyStateConfigs[resourceKey] || {
      resource: resourceKey,
      title: `No ${resourceKey.replace(/[/-]/g, ' ')} Yet`,
      subtitle: 'Records will appear here once your team starts adding them.',
      actionText: 'Create',
      navigationPath: `/${resourceKey}/create`,
    };
    if (!config) {
      console.warn(`No empty state configuration found for resource: ${resourceKey}`);
      return null;
    }

    return (
      <CustomEmptyState
        resource={config.resource}
        title={translate(`empty_states.${resourceKey}.title`, { defaultValue: config.title })}
        subtitle={translate(`empty_states.${resourceKey}.subtitle`, {
          defaultValue: config.subtitle,
        })}
        actionText={translate(`empty_states.${resourceKey}.action`, {
          defaultValue: config.actionText,
        })}
        onAction={() => navigate(config.navigationPath)}
      />
    );
  };
};
